import { getMonthName } from "@/utils/date";

/**
 * Converts array data to {x, y} format for linear x-axis
 */
export const toXYData = (arr) => arr.map((y, x) => (y !== null ? { x, y } : null)).filter(Boolean);

/**
 * Generates array of 13 month names starting from current month
 */
export const generateMonthArray = () => {
  const currentMonth = new Date().getMonth() + 1;
  const months = [];
  for (let i = 0; i < 12; i++) {
    months.push(getMonthName(((currentMonth + i - 1) % 12) + 1));
  }
  months.push(getMonthName(currentMonth));
  return months;
};

/**
 * Formats a numeric value as currency
 */
export const formatCurrency = (value, round = false) => {
  if (value == null) return null;
  return `$ ${new Intl.NumberFormat("en-US", { minimumFractionDigits: round ? 0 : 2 }).format(round ? Math.round(value) : value)}`;
};

/**
 * Calculates the minimum tick value for the y-axis
 */
export const calculateMinTickValue = (minValue, stepSize) => {
  if (!isFinite(minValue) || !minValue) return 0;
  const tempMin = Math.floor(minValue / stepSize) * stepSize;
  return tempMin < 0 ? 0 : tempMin;
};

/**
 * Calculates the maximum tick value for the y-axis
 */
export const calculateMaxTickValue = (maxValue, stepSize, minTickValue) => {
  if (!isFinite(maxValue) || !maxValue) return stepSize * 5;
  const calculatedMax = Math.round(maxValue / stepSize + 1) * stepSize;
  const minRange = stepSize * 2;
  if (calculatedMax - minTickValue < minRange) {
    return minTickValue + minRange;
  }
  return calculatedMax > minTickValue ? calculatedMax : minTickValue + minRange;
};

/**
 * Processes company loads into scatter points with x/y coordinates
 */
export const processCompanyLoadsScatterData = (companyLoads, rateData) => {
  if (!companyLoads?.length || !rateData?.length) return [];

  return companyLoads
    .map((load) => {
      const { pickupDate, price, loadId, broker } = load;

      const loadYear = Number(pickupDate.year);
      const loadMonth = Number(pickupDate.month);
      const loadDay = Number(pickupDate.day);

      const monthIndex = rateData.findIndex((r) => Number(r.year) === loadYear && Number(r.month) === loadMonth);

      if (monthIndex === -1) return null;

      const monthRateData = rateData[monthIndex];
      const daysInMonth = new Date(loadYear, loadMonth, 0).getDate();
      const dayFraction = (loadDay - 1) / daysInMonth;
      const x = monthIndex + dayFraction;

      return {
        x,
        y: parseInt(price),
        loadId,
        pickupDate,
        price,
        broker,
        monthMin: monthRateData.minPrice,
        monthMax: monthRateData.maxPrice,
        monthMedian: monthRateData.avgPrice,
      };
    })
    .filter(Boolean);
};

/**
 * Creates chart datasets configuration
 */
export const createChartDatasets = ({
  maxValue,
  minRangeData,
  maxRangeData,
  flatData,
  showCompanyReports,
  companyLoadsScatterData,
  showForecast,
  forecastData,
  forecastMinRangeData,
  forecastMaxRangeData,
  isDarkTheme,
}) => {
  const datasets = [];
  const hasMinMaxData = minRangeData.some((v) => v !== null) && maxRangeData.some((v) => v !== null);
  const hasForecastData = showForecast && forecastData?.some((v) => v !== null);
  const hasForecastMinMaxData =
    hasForecastData && forecastMinRangeData?.some((v) => v !== null) && forecastMaxRangeData?.some((v) => v !== null);
  const forecastColor = isDarkTheme ? "#9AB8F4" : "#648CDB";
  const forecastRangeColor = isDarkTheme ? "rgba(31, 81, 122, 0.4)" : "rgba(191, 226, 255, 0.15)";

  if (!maxValue || maxValue <= 0) return datasets;

  // Min-Max range shaded area
  if (hasMinMaxData) {
    datasets.push({
      type: "line",
      label: "maxRange",
      data: toXYData(maxRangeData),
      borderColor: "transparent",
      backgroundColor: "transparent",
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      borderWidth: 0,
      fill: false,
      spanGaps: false,
      datalabels: { display: false },
      order: 3,
    });

    datasets.push({
      type: "line",
      label: "minRange",
      data: toXYData(minRangeData),
      borderColor: "transparent",
      backgroundColor: "rgba(36, 185, 140, 0.15)",
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      borderWidth: 0,
      fill: "-1",
      spanGaps: false,
      datalabels: { display: false },
      order: 3,
    });
  }

  // Forecast min-max range shaded area
  if (hasForecastMinMaxData) {
    datasets.push({
      type: "line",
      label: "forecastMaxRange",
      data: toXYData(forecastMaxRangeData),
      borderColor: "transparent",
      backgroundColor: "transparent",
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      borderWidth: 0,
      fill: false,
      spanGaps: false,
      datalabels: { display: false },
      order: 3,
    });

    datasets.push({
      type: "line",
      label: "forecastMinRange",
      data: toXYData(forecastMinRangeData),
      borderColor: "transparent",
      backgroundColor: forecastRangeColor,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 0,
      borderWidth: 0,
      fill: "-1",
      spanGaps: false,
      datalabels: { display: false },
      order: 3,
    });
  }

  // Forecast dashed line dataset
  if (hasForecastData) {
    datasets.push({
      type: "line",
      label: "forecast",
      data: toXYData(forecastData),
      borderColor: forecastColor,
      backgroundColor: "transparent",
      fill: false,
      spanGaps: false,
      borderDash: [5, 5],
      borderCapStyle: "round",
      pointRadius: 3,
      hoverRadius: 5,
      pointBackgroundColor: forecastColor,
      pointHoverBackgroundColor: forecastColor,
      borderWidth: 2,
      pointBorderWidth: 0,
      pointBorderColor: forecastColor,
      pointHoverBorderColor: forecastColor,
      datalabels: { display: false },
      order: 2,
    });
  }

  // Market line dataset
  datasets.push({
    type: "line",
    label: "market",
    data: toXYData(flatData),
    borderColor: "#24B98C",
    backgroundColor: "transparent",
    fill: false,
    spanGaps: false,
    pointRadius: 4,
    hoverRadius: 6,
    pointBackgroundColor: "#24B98C",
    pointHoverBackgroundColor: "#2BBC90",
    borderWidth: 2,
    pointBorderWidth: 2,
    pointHoverBorderColor: "#121815",
    pointBorderColor: "#24B98C",
    datalabels: { display: false },
    order: 1,
  });

  // Company loads scatter dataset
  if (showCompanyReports && companyLoadsScatterData.length > 0) {
    const companyLoadColor = isDarkTheme ? "#DA9453" : "#FFC187";
    datasets.push({
      type: "scatter",
      label: "companyLoads",
      data: companyLoadsScatterData,
      backgroundColor: "transparent",
      borderColor: companyLoadColor,
      pointRadius: 4,
      hoverRadius: 5,
      pointBackgroundColor: "transparent",
      pointHoverBackgroundColor: "transparent",
      pointBorderWidth: 1,
      pointHoverBorderWidth: 1,
      pointBorderColor: companyLoadColor,
      pointHoverBorderColor: companyLoadColor,
      datalabels: { display: false },
      order: 0,
    });
  }

  return datasets;
};

/**
 * Creates chart options configuration
 */
export const createChartOptions = ({ maxValue, minTickValue, maxTickValue, stepSize, months, isDarkTheme, externalTooltipHandler }) => ({
  responsive: true,
  maintainAspectRatio: false,
  height: 309,
  layout: {
    padding: {
      bottom: 0,
    },
  },
  interaction: {
    mode: "nearest",
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: {
      mode: "nearest",
      intersect: false,
      enabled: false,
      external: externalTooltipHandler,
    },
  },
  hover: {
    mode: "nearest",
    intersect: false,
  },
  scales: {
    x: {
      type: "linear",
      title: { display: false },
      min: 0,
      max: months.length - 1,
      ticks: {
        stepSize: 1,
        font: { size: 14, family: "Saira" },
        crossAlign: "near",
        color: isDarkTheme ? "#838A85" : "#979B98",
        padding: 4,
        callback: (value) => {
          if (Number.isInteger(value) && value >= 0 && value < months.length) {
            return months[value];
          }
          return "";
        },
      },
      grid: {
        display: !!maxValue,
        color: isDarkTheme ? "#282928" : "#E5E9EB",
        drawTicks: true,
        tickLength: 8,
        tickColor: "transparent",
        borderDash: isDarkTheme ? [4, 4] : [],
      },
      border: { display: false },
    },
    y: {
      display: !!maxValue,
      title: { display: false },
      min: minTickValue,
      max: maxTickValue,
      ticks: {
        color: isDarkTheme ? "#838A85" : "#979B98",
        padding: 5,
        crossAlign: "far",
        font: { size: 14, family: "Saira" },
        stepSize,
        maxTicksLimit: 5,
        callback: (value) => new Intl.NumberFormat("en-US").format(value ? Math.round(value) : value),
      },
      grid: {
        color: isDarkTheme ? "#282928" : "#E5E9EB",
        display: !!maxValue,
        drawTicks: false,
        borderDash: isDarkTheme ? [4, 4] : [],
      },
    },
  },
  animation: {
    duration: 300,
    easing: "easeInOutQuad",
  },
});
